import { useState } from 'react';
import { Select, SelectItem, Spinner } from '@heroui/react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid,
} from 'recharts';
import { PageHeader } from '@/components/common/PageHeader';
import { StatTile } from '@/components/common/StatTile';
import { useStatistics } from '@/features/statistics/hooks';
import { formatBytes, formatCount } from '@/lib/format';
import { EnvelopeIcon, PaperAirplaneIcon, NoSymbolIcon, ShieldExclamationIcon, InboxIcon, GlobeAltIcon, AtSymbolIcon, ArrowsRightLeftIcon } from '@/icons';

const WINDOWS = [{ k: '24', label: 'Last 24 hours' }, { k: '168', label: 'Last 7 days' }, { k: '720', label: 'Last 30 days' }];
// Chart colors come from the theme tokens so they recolor in dark mode.
const cssVar = (name: string) => `rgb(var(${name}))`;

export function StatisticsPage() {
  const [win, setWin] = useState('24');
  const { data, isLoading } = useStatistics(Number(win));

  const volume = data ? [
    { name: 'Delivered', value: data.delivered, fill: cssVar('--nh-success') },
    { name: 'Sent', value: data.sent, fill: cssVar('--nh-info') },
    { name: 'Rejected', value: data.rejected, fill: cssVar('--nh-warning') },
    { name: 'Spam', value: data.spam, fill: cssVar('--nh-danger') },
  ] : [];
  const composition = data ? [
    { name: 'Mailboxes', value: data.mailboxes, fill: cssVar('--nh-brand') },
    { name: 'Aliases', value: data.aliases, fill: cssVar('--nh-info') },
    { name: 'Forwarders', value: data.forwarders, fill: cssVar('--nh-success') },
  ] : [];

  return (
    <div>
      <PageHeader title="Statistics" description="Mail volume and object analytics for your tenant."
        actions={
          <Select aria-label="Time window" size="sm" className="w-44" selectedKeys={new Set([win])} onSelectionChange={(k) => setWin(String(Array.from(k)[0] ?? '24'))}>
            {WINDOWS.map((w) => <SelectItem key={w.k}>{w.label}</SelectItem>)}
          </Select>
        } />

      {isLoading ? (
        <div className="flex justify-center py-20"><Spinner label="Loading statistics…" /></div>
      ) : data ? (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <div className="rounded-xl border border-border bg-surface p-5"><StatTile label="Delivered" value={formatCount(data.delivered)} icon={EnvelopeIcon} accent="success" /></div>
            <div className="rounded-xl border border-border bg-surface p-5"><StatTile label="Sent" value={formatCount(data.sent)} icon={PaperAirplaneIcon} accent="info" /></div>
            <div className="rounded-xl border border-border bg-surface p-5"><StatTile label="Rejected" value={formatCount(data.rejected)} icon={NoSymbolIcon} accent="warning" /></div>
            <div className="rounded-xl border border-border bg-surface p-5"><StatTile label="Spam" value={formatCount(data.spam)} icon={ShieldExclamationIcon} accent="danger" /></div>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="rounded-xl border border-border bg-surface p-5">
              <h3 className="mb-4 text-sm font-semibold text-fg">Mail volume ({data.window})</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={volume}>
                  <CartesianGrid strokeDasharray="3 3" stroke={cssVar('--nh-border')} vertical={false} />
                  <XAxis dataKey="name" tick={{ fill: cssVar('--nh-fg-muted'), fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: cssVar('--nh-fg-muted'), fontSize: 12 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: cssVar('--nh-surface-raised'), border: `1px solid ${cssVar('--nh-border')}`, borderRadius: 8, color: cssVar('--nh-fg') }} />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="rounded-xl border border-border bg-surface p-5">
              <h3 className="mb-4 text-sm font-semibold text-fg">Object composition</h3>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={composition} dataKey="value" nameKey="name" innerRadius={60} outerRadius={100} paddingAngle={2}>
                    {composition.map((c, i) => <Cell key={i} fill={c.fill} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: cssVar('--nh-surface-raised'), border: `1px solid ${cssVar('--nh-border')}`, borderRadius: 8, color: cssVar('--nh-fg') }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-2 flex justify-center gap-4 text-xs text-fg-muted">
                {composition.map((c) => (
                  <span key={c.name} className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: c.fill }} />{c.name}</span>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <div className="rounded-xl border border-border bg-surface p-5"><StatTile label="Domains" value={formatCount(data.domains)} icon={GlobeAltIcon} accent="brand" sublabel={`${data.verifiedDomains} verified`} /></div>
            <div className="rounded-xl border border-border bg-surface p-5"><StatTile label="Mailboxes" value={formatCount(data.mailboxes)} icon={InboxIcon} accent="brand" /></div>
            <div className="rounded-xl border border-border bg-surface p-5"><StatTile label="Aliases" value={formatCount(data.aliases)} icon={AtSymbolIcon} accent="info" /></div>
            <div className="rounded-xl border border-border bg-surface p-5"><StatTile label="Storage used" value={formatBytes(data.storageUsedBytes)} icon={ArrowsRightLeftIcon} accent="success" sublabel={`of ${formatBytes(data.storageQuotaBytes)}`} /></div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
